import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from 'src/components/LoadingButton';
import BackButton from 'src/components/BackButton';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import { useDeploymentRings } from 'src/hooks/deploymentRing';
import { useTagOptions } from 'src/hooks/tag';
import { formatDate } from 'src/utils/formatTime';
import { DEVICE_STATUS, getDeviceStatusOptions } from 'src/utils/enums/deviceStatus';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import { notesValidation, tagsValidation } from 'src/validations/deviceValidation';

const schema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  notes: notesValidation,
  tags: tagsValidation,
  deploymentRingId: Yup.mixed().nullable(),
});

const toFormTags = items =>
  (items || [])
    .map(item => (typeof item === 'string' ? { name: item.trim() } : item))
    .filter(item => (item?.name || '').trim());

const DeviceSettingsModal = ({ open, onClose, device, tags, onSave, isSaving }) => {
  const [tagSearch, setTagSearch] = useState('');
  const defaultValues = useMemo(
    () => ({
      status: device?.status || DEVICE_STATUS.Unknown,
      notes: device?.notes || '',
      tags: tags || [],
      deploymentRingId: device?.deploymentRingId ?? '',
    }),
    [device?.deploymentRingId, device?.notes, device?.status, tags],
  );

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset } = formMethods;
  const { data: tagOptionsData, isLoading: tagOptionsLoading } = useTagOptions({
    search: tagSearch,
  });
  const tagOptions = useMemo(() => tagOptionsData?.tags || [], [tagOptionsData]);

  const { data: deploymentRingsData } = useDeploymentRings({ enabled: open });
  // Only active rings can be picked, but a device's current ring always stays in the list so the
  // dropdown can show it (and a ring that hasn't loaded yet shows a placeholder, not a blank).
  const deploymentRingOptions = useMemo(() => {
    const currentRingId = device?.deploymentRingId;
    const options = (deploymentRingsData?.rings || [])
      .filter(ring => ring.isActive || ring.id === currentRingId)
      .map(ring => ({
        value: ring.id,
        label: ring.isDefault ? `${ring.name} (Default)` : ring.name,
      }));

    if (currentRingId && !options.some(option => option.value === currentRingId)) {
      options.unshift({ value: currentRingId, label: 'Loading...' });
    }

    return options;
  }, [deploymentRingsData, device?.deploymentRingId]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setTagSearch('');
    }
  }, [open, reset, defaultValues]);

  const submit = async data => {
    await onSave({
      status: data.status,
      notes: data.notes,
      tags: data.tags || [],
      deploymentRingId: data.deploymentRingId === '' ? undefined : Number(data.deploymentRingId),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle>Device Settings</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <form autoComplete="off" onSubmit={handleSubmit(submit)}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField label="Name" value={device?.name || ''} fullWidth disabled size="small" />
              <TextField
                label="Unique External Id"
                value={device?.uniqueExternalId || ''}
                fullWidth
                disabled
                size="small"
              />
            </Stack>

            <HookFormTextField
              name="status"
              label="Status"
              formMethods={formMethods}
              select
              size="small"
              fullWidth
              options={getDeviceStatusOptions()}
            />

            <Controller
              name="tags"
              control={formMethods.control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  freeSolo
                  options={tagOptions}
                  loading={tagOptionsLoading}
                  value={field.value || []}
                  onChange={(_, newValue) => {
                    field.onChange(toFormTags(newValue));
                  }}
                  onInputChange={(_, newInput, reason) => {
                    if (reason === 'input') {
                      setTagSearch(newInput);
                    }
                  }}
                  getOptionLabel={option =>
                    typeof option === 'string' ? option : option?.name || ''
                  }
                  isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Tags"
                      size="small"
                      fullWidth
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            />

            <HookFormTextField
              name="notes"
              label="Notes"
              formMethods={formMethods}
              multiline
              minRows={2}
              maxRows={6}
              size="small"
              fullWidth
            />

            <Box>
              <HookFormTextField
                name="deploymentRingId"
                label="Deployment Ring"
                formMethods={formMethods}
                select
                size="small"
                fullWidth
                options={deploymentRingOptions}
                helperText="The ring this device belongs to. Its deferral days set how long policies wait to deploy to the device."
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                OS Details
              </Typography>
              <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
                <TextField
                  label="OS Version"
                  value={device?.osVersion || ''}
                  fullWidth
                  disabled
                  size="small"
                />
                <TextField
                  label="OS SKU"
                  value={device?.osSku || ''}
                  fullWidth
                  disabled
                  size="small"
                />
                <TextField
                  label="OS Type"
                  value={device?.osType || ''}
                  fullWidth
                  disabled
                  size="small"
                />
              </Stack>
            </Box>

            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField
                label="Log Collector Version"
                value={device?.logCollectorVersion || ''}
                fullWidth
                disabled
                size="small"
              />
              <TextField
                label="WDAC Status"
                value={getWdacStatusLabel(device?.wdacStatus)}
                fullWidth
                disabled
                size="small"
              />
            </Stack>

            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField
                label="Created"
                value={device?.createdAt ? formatDate(device.createdAt) : ''}
                fullWidth
                disabled
                size="small"
              />
              <TextField
                label="Updated"
                value={device?.updatedAt ? formatDate(device.updatedAt) : ''}
                fullWidth
                disabled
                size="small"
              />
            </Stack>

            <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
              <TextField
                label="Last Contact"
                value={device?.lastContactDate ? formatDate(device.lastContactDate) : ''}
                fullWidth
                disabled
                size="small"
              />
              <TextField
                label="WDAC Last Heartbeat"
                value={
                  device?.lastWdacHeartbeatDate ? formatDate(device.lastWdacHeartbeatDate) : ''
                }
                fullWidth
                disabled
                size="small"
              />
            </Stack>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BackButton disabled={isSaving} onClick={onClose}>
          Cancel
        </BackButton>
        <LoadingButton variant="contained" onClick={handleSubmit(submit)} loading={isSaving}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

DeviceSettingsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  device: PropTypes.object,
  tags: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default DeviceSettingsModal;
